import {
    addCalendarDays,
    fullDateFullYearFormat,
    isDateOnlyBefore,
} from "@common/helpers/date";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import type { DatePickerPreset } from "@design-system/DatePicker/DatePicker.types";
import { formatDateOnly } from "@helpers/date";

type StatementDates = Pick<
    FinancialDocumentExtended,
    "id" | "financialAccountId" | "closingDate"
>;

interface Props {
    statement: StatementDates;
    statements: StatementDates[];
}

const findPreviousClosingDate = ({ statement, statements }: Props) => {
    const { closingDate, financialAccountId } = statement;
    if (financialAccountId == null || !closingDate) {
        return undefined;
    }
    const earlierClosingDates = statements.flatMap((other) => {
        const otherClosingDate = other.closingDate;
        if (!otherClosingDate) {
            return [];
        }
        const isEarlierStatementOnSameAccount =
            other.id !== statement.id &&
            other.financialAccountId === financialAccountId &&
            isDateOnlyBefore(otherClosingDate, closingDate);
        return isEarlierStatementOnSameAccount ? [otherClosingDate] : [];
    });
    return earlierClosingDates.sort().at(-1);
};

const MIN_MONTHLY_PERIOD_DAYS = 25;
const MAX_MONTHLY_PERIOD_DAYS = 35;

const isMonthlyPeriod = ({
    openingDate,
    closingDate,
}: {
    openingDate: string;
    closingDate: string;
}) =>
    !isDateOnlyBefore(
        openingDate,
        addCalendarDays(closingDate, 1 - MAX_MONTHLY_PERIOD_DAYS),
    ) &&
    !isDateOnlyBefore(
        addCalendarDays(closingDate, 1 - MIN_MONTHLY_PERIOD_DAYS),
        openingDate,
    );

export const getOpeningDatePresets = (
    props: Props,
): ReadonlyArray<DatePickerPreset<string>> => {
    const { closingDate } = props.statement;
    const previousClosingDate = findPreviousClosingDate(props);
    if (!closingDate || !previousClosingDate) {
        return [];
    }
    const suggestedOpeningDate = addCalendarDays(previousClosingDate, 1);
    if (!isMonthlyPeriod({ openingDate: suggestedOpeningDate, closingDate })) {
        return [];
    }
    const formattedClosingDate = formatDateOnly(
        previousClosingDate,
        fullDateFullYearFormat,
    );
    return [
        {
            label: `Day after ${formattedClosingDate}`,
            value: suggestedOpeningDate,
        },
    ];
};
